import { Modal, Button, Form } from 'react-bootstrap';
import { useEffect, useRef, useState } from 'react';
import { MailTemplateVariable, TemplatesModel } from 'types';
import { AuthGet, AuthPut } from 'helpers/api/AuthCRUD';
import { API } from 'helpers/api/endpoints';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import MailTemplateVariablesPanel from './MailTemplateVariablesPanel';
import MailRichTextEditor from './MailRichTextEditor';
import { env_backend_url } from 'env_Variables';
import { useTranslation } from 'react-i18next';
import MailImageLinkModal from './MailImageLinkModal';

type Props = {
  template: TemplatesModel;
  onClose: () => void;
  onSaved: () => void;
  readOnly?: boolean;
};

export default function MailTemplateEditModal({ template, onClose, onSaved, readOnly }: Props) {

  const { t } = useTranslation(['labels', 'mailManager']);
  const { token } = useSelector((state: RootState) => state);
  const [variables, setVariables] = useState<MailTemplateVariable[]>([]);
  const [subject, setSubject] = useState(template.subject);
  const [bodyHtml, setBodyHtml] = useState(template.bodyHtml);
  const editorRef = useRef<any>(null);
  const uploadedImagesRef = useRef<string[]>([]);
  const originalImagesRef = useRef<string[]>([]);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [imageLink, setImageLink] = useState('');
  const [expiryWarningDays, setExpiryWarningDays] = useState(
    template.expiryWarningDays ?? 90
  );

  useEffect(() => {
    setExpiryWarningDays(template.expiryWarningDays ?? 90);
  }, [template.guid]);


  const insertVariable = (key: string) => {
    const editor = editorRef.current;
    if (!editor) return;

    editor.model.change((writer: any) => {
      const viewFragment = editor.data.processor.toView(
        `<span class="mail-var" data-key="${key}" contenteditable="false">{{${key}}}</span>&nbsp;`
      );
      const modelFragment = editor.data.toModel(viewFragment);
      editor.model.insertContent(modelFragment);
      const selection = editor.model.document.selection;
      const positionAfter = selection.getLastPosition();

      if (positionAfter) {
        writer.setSelection(positionAfter);
      }
    });
    editor.editing.view.focus();
  };

  
    const applyResetLinkToSelection = () => {
      const editor = editorRef.current;
      if (!editor) return;
  
      editor.model.change((writer: any) => {
        const selection = editor.model.document.selection;
        const range = selection.getFirstRange();
        if (!range) return;
  
        for (const item of range.getItems()) {
          if (item.is('textProxy')) {
            writer.setAttribute('linkHref', '{{ResetLink}}', item);
          }
        }
      });
    };

  const applyLinkToSelection = () => {
    const editor = editorRef.current;
    if (!editor || !imageLink) return;

    editor.model.change((writer: any) => {
      const selection = editor.model.document.selection;
      const selectedElement = selection.getSelectedElement();

      if (selectedElement && selectedElement.is('element', 'imageBlock')) {
        writer.setAttribute('linkHref', imageLink, selectedElement);
        return;
      }

      const range = selection.getFirstRange();
      if (!range) return;

      for (const item of range.getItems()) {
        if (item.is('textProxy')) {
          writer.setAttribute('linkHref', imageLink, item);
        }
      }
    });

    setImageLink('');
    setShowLinkModal(false);
  };



  useEffect(() => {
    originalImagesRef.current = extractImageUrls(template.bodyHtml);
  }, [template.guid]);

  function extractImageUrls(html: string): string[] {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return Array.from(doc.querySelectorAll('img'))
      .map(img => img.getAttribute('src'))
      .filter(Boolean) as string[];
  }

  useEffect(() => {
    if (!token) return;

    AuthGet(API.mail.templateVariablesByGuid(template.guid), token)
      .then(r => Array.isArray(r) && setVariables(r))
      .catch(console.error);

  }, [token, template.guid]);

  const onSave = async () => {
    if (!token) return;

    const currentImages = extractImageUrls(bodyHtml);
    const removedImages = originalImagesRef.current.filter(
      img => !currentImages.includes(img)
    );

    await AuthPut(
      API.mail.updateTemplate(template.guid),
      {
        ...template,
        subject,
        bodyHtml,
        expiryWarningDays
      },
      token
    );

    if (removedImages.length) {
      await fetch(`${env_backend_url}${API.mail.cleanupImages}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ images: removedImages })
      });
    }

    onSaved();
    onClose();
  };

  const onCancel = async () => {
    if (uploadedImagesRef.current.length && token) {
      await fetch(`${env_backend_url}${API.mail.cleanupImages}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          images: uploadedImagesRef.current
        })
      });
    }

    onClose();
  };


  return (
    
    <Modal show onHide={onClose} size="lg" enforceFocus={false}>
      <MailImageLinkModal
          show={showLinkModal}
          value={imageLink}
          onChange={setImageLink}
          onApply={applyLinkToSelection}
          onClose={() => setShowLinkModal(false)}
        />
      <Modal.Header closeButton>
        <Modal.Title>
          {t(readOnly ? 'preview_mail' : 'edit_mail', { ns: 'mailManager' })} {template.templateKey}
        </Modal.Title>

      </Modal.Header>

      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>{t('subject', { ns: 'mailManager' })}</Form.Label>
          <Form.Control
            value={subject}
            onChange={e => setSubject(e.target.value)}
          />
        </Form.Group>

        <Form.Group>
          <Form.Label>{t('body', { ns: 'mailManager' })}</Form.Label>

          <MailRichTextEditor
            value={bodyHtml}
            onChange={setBodyHtml}
            onReady={(ed) => (editorRef.current = ed)}
            token={token!}
            onImageUploaded={(url) => {
              uploadedImagesRef.current.push(url);
            }}
            readOnly={readOnly}
          />

          <MailTemplateVariablesPanel
            variables={variables}
            onInsert={insertVariable}
            onInsertResetLink={applyResetLinkToSelection}
            disabled={readOnly}
          />
        </Form.Group>

        {/* <Button
          size="sm"
          variant="outline-primary"
          onClick={() => setShowLinkModal(true)}
        >
          {t('add_link', { ns: 'mailManager' })}
        </Button> */}

      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel}>
          {t('close', { ns: 'labels' })}
        </Button>

        {!readOnly && (
          <Button variant="primary" onClick={onSave}>
            {t('save', { ns: 'mailManager' })}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
